import {
  createContext,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';
import useMergedRef from '@react-hook/merged-ref';
import { createPortal } from 'react-dom';
import { useShadowGlobalProps } from './shadow-global-props';
import { type WrapFn } from './types';

export interface ShadowAPI {
  root: ShadowRoot | undefined;
}

export interface ShadowProps {
  mode?: ShadowRootMode;
  wrap?: WrapFn;
  className?: string;
  children?: ReactNode;
}

export const Shadow = forwardRef<HTMLDivElement, ShadowProps>(function Shadow(
  { mode, wrap, className, children },
  ref
) {
  const globalProps = useShadowGlobalProps();

  const [finalMode] = useState(mode ?? globalProps?.mode ?? 'open');
  const [root, setRoot] = useState<ShadowRoot | undefined>(undefined);

  // This element acts as a placeholder to prevent the host element
  // from being hidden or targeted by the :empty CSS selector.
  const [unemptier] = useState(() => document.createElement('span'));

  const attach = useCallback((host: Element | null) => {
    if (host) {
      host.appendChild(unemptier);
      setRoot(host.attachShadow({ mode: finalMode }));
    } else {
      unemptier.remove();
      setRoot(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- finalMode and unemptier never change
  }, []);

  const mountingInventoryAPI = useMountingInventoryAPI();
  useEffect(() => {
    if (mountingInventoryAPI && root) {
      return mountingInventoryAPI.addRoot(root);
    }

    return undefined;
  }, [mountingInventoryAPI, root]);

  const api: ShadowAPI = useMemo(() => {
    return { root };
  }, [root]);

  const mergedRef = useMergedRef(attach, ref);

  let portal: ReactNode | null = null;

  if (root) {
    const finalWrap = wrap ?? globalProps?.wrap;
    const finalChildren = finalWrap ? finalWrap(root, children) : children;
    portal = createPortal(finalChildren, root);
  }

  return (
    <div ref={mergedRef} className={className}>
      <ShadowAPIContext.Provider value={api}>
        {portal}
      </ShadowAPIContext.Provider>
    </div>
  );
});

const ShadowAPIContext = createContext<ShadowAPI | undefined>(undefined);

export function useShadowAPI() {
  return useContext(ShadowAPIContext);
}
